import ystnjEnvironmentAuthority from "../../../config/deploy/ystnj-environment-authority.json";

const normalizeText = (value: unknown): string => String(value ?? "").trim();
const normalizeEmail = (value: unknown): string =>
  normalizeText(value).toLowerCase();
const normalizeOrigin = (value: unknown): string => {
  const text = normalizeText(value);
  if (!text) return "";
  try {
    return new URL(text).origin.toLowerCase();
  } catch {
    return text.replace(/\/+$/, "").toLowerCase();
  }
};

type EnvironmentRecord =
  (typeof ystnjEnvironmentAuthority.environments)[number];

export type YstnjSuperAdminIdentityPolicy = {
  environmentId: string;
  userPoolId: string;
  branch: string;
  requiredProvider: string;
  requireVerifiedEmail: boolean;
  requiredEmailDomain: string;
  allowlist: string[];
};

const environmentOrigins = (environment: EnvironmentRecord): string[] =>
  Array.from(
    new Set(
      [
        ...(Array.isArray(environment.auth?.origins)
          ? environment.auth.origins
          : []),
        normalizeText(environment.amplify?.hostedOrigin),
        ...(Array.isArray(environment.amplify?.hostAliases)
          ? environment.amplify.hostAliases
          : []),
      ]
        .map(normalizeOrigin)
        .filter(Boolean),
    ),
  );

export const resolveYstnjSuperAdminIdentityPolicy = (context: {
  environmentId?: string | null;
  userPoolId?: string | null;
  origin?: string | null;
  branch?: string | null;
}): YstnjSuperAdminIdentityPolicy | null => {
  const normalizedEnvironmentId = normalizeText(context.environmentId);
  const normalizedUserPoolId = normalizeText(context.userPoolId);
  const normalizedOrigin = normalizeOrigin(context.origin);
  const normalizedBranch = normalizeText(context.branch);
  if (
    !normalizedEnvironmentId &&
    !normalizedUserPoolId &&
    !normalizedOrigin &&
    !normalizedBranch
  ) {
    return null;
  }

  const matches = ystnjEnvironmentAuthority.environments.filter(
    (environment) => {
      if (environment.status !== "active") return false;
      if (
        normalizedEnvironmentId &&
        environment.environmentId !== normalizedEnvironmentId
      ) {
        return false;
      }
      if (
        normalizedUserPoolId &&
        normalizeText(environment.auth?.userPoolId) !== normalizedUserPoolId
      ) {
        return false;
      }
      if (
        normalizedOrigin &&
        !environmentOrigins(environment).includes(normalizedOrigin)
      ) {
        return false;
      }
      if (
        normalizedBranch &&
        normalizeText(environment.amplify?.branch) !== normalizedBranch
      ) {
        return false;
      }
      return true;
    },
  );
  if (matches.length !== 1) return null;

  const environment = matches[0];
  const identity = environment.superAdminIdentity;
  const allowlist = Array.from(
    new Set(
      (identity?.allowlistValues ?? []).map(normalizeEmail).filter(Boolean),
    ),
  );
  const branch = normalizeText(environment.amplify?.branch);
  const userPoolId = normalizeText(environment.auth?.userPoolId);
  const requiredProvider = normalizeText(
    identity?.requiredProvider,
  ).toLowerCase();
  const requiredEmailDomain = normalizeText(
    identity?.requiredEmailDomain,
  ).toLowerCase();
  const hasWildcard = allowlist.includes("*");
  const hasSafeDomainWildcard =
    hasWildcard &&
    requiredProvider === "google" &&
    identity?.requireVerifiedEmail === true &&
    requiredEmailDomain === "intelliolabs.com";
  if (
    !userPoolId ||
    !branch ||
    !requiredProvider ||
    !requiredEmailDomain ||
    allowlist.length === 0 ||
    (hasWildcard && !hasSafeDomainWildcard)
  ) {
    return null;
  }

  return {
    environmentId: environment.environmentId,
    userPoolId,
    branch,
    requiredProvider,
    requireVerifiedEmail: identity?.requireVerifiedEmail === true,
    requiredEmailDomain,
    allowlist,
  };
};

export const resolveYstnjSuperAdminAllowlist = (context: {
  environmentId?: string | null;
  userPoolId?: string | null;
  origin?: string | null;
  branch?: string | null;
}): string[] => resolveYstnjSuperAdminIdentityPolicy(context)?.allowlist ?? [];

export const isYstnjSuperAdminEmail = ({
  email,
  environmentId,
  userPoolId,
  origin,
  branch,
}: {
  email?: string | null;
  environmentId?: string | null;
  userPoolId?: string | null;
  origin?: string | null;
  branch?: string | null;
}): boolean => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return false;
  const policy = resolveYstnjSuperAdminIdentityPolicy({
    environmentId,
    userPoolId,
    origin,
    branch,
  });
  if (!policy) return false;
  if (policy.allowlist.includes(normalizedEmail)) return true;
  if (!policy.allowlist.includes("*")) return false;
  return normalizedEmail.split("@").pop() === policy.requiredEmailDomain;
};
